import { MapZonesOverlay } from 'const/Map/MapZonesOverlay';
import { MapZonesType } from 'const/Map/MapZonesType';
import { FeatureCollection } from 'geojson';
import {
  EventData,
  FillLayer,
  GeoJSONSource,
  LineLayer,
  Map,
  MapboxGeoJSONFeature,
  MapMouseEvent,
  SymbolLayer,
} from 'mapbox-gl';
import React, { useEffect, useRef } from 'react';
import { PartitionsAreaLayers } from './PartitionsAreaLayers';
import { PreferredAreaLayers } from './PreferredAreaLayers';

export interface ZonesOverlayFeatureStateObject {
  id: string;
  value: Record<string, any>;
}

interface ZonesOverlayProps {
  map: Map;
  data: FeatureCollection;
  type: MapZonesType;
  objects: Array<ZonesOverlayFeatureStateObject>;
  countData?: Record<string, number>;
  hideLabel?: boolean;
  onClick: (selectedZone: string | number, currentFeatureState: any) => void;
}

export const ZonesOverlay = ({ map, ...props }: ZonesOverlayProps) => {
  const dataRef = useRef<FeatureCollection>();

  const hoveredZoneId = useRef<ZonesOverlayFeatureStateObject>();

  /* This `mapHover` is used for cleanup the old hover whenever the Layers are re-assigned */
  const mapHover = useRef<() => void>();

  /* assignLayers */
  /* Create the Layers for the ZonesOverlay */
  useEffect(() => {
    /* Only re-assign layers when the data is different */
    if (JSON.stringify(props.data) === JSON.stringify(dataRef.current)) {
      return;
    }

    dataRef.current = props.data;

    const zonesDataFeatures =
      dataRef.current.features.map(feature => ({
        ...feature,
        properties: {
          ...feature.properties,
          count: props.countData[feature.id || feature.properties.id] ?? 0,
        },
      })) || [];

    const zonesData = { ...dataRef.current, features: zonesDataFeatures };

    /* Wrapper for Zones */
    if (map.getSource(MapZonesOverlay.SourceId)) {
      (map.getSource(MapZonesOverlay.SourceId) as GeoJSONSource).setData(
        zonesData
      );
    } else {
      map.addSource(MapZonesOverlay.SourceId, {
        type: 'geojson',
        data: zonesData,
      });
    }

    const fillLayer = map.getLayer(MapZonesOverlay.FillLayerId);
    const borderLayer = map.getLayer(MapZonesOverlay.BorderLayerId);
    const labelLayer = map.getLayer(MapZonesOverlay.LabelLayerId);

    /**
     * Note :
     * Do not remove any Layers in this ZoneOverlay.
     * We will only assign Layers when it's not assigned yet.
     * We don't want to remove the layers whenever it's re-rendered, because we will lose the `featureState`,
     * and it will causing the Layers blinking or even not showing it
     */
    if (zonesData.features.length) {
      switch (props.type) {
        case MapZonesType.PreferredArea:
          !fillLayer && map.addLayer(PreferredAreaLayers.fills as FillLayer);
          !borderLayer &&
            map.addLayer(PreferredAreaLayers.borders as LineLayer);
          !labelLayer &&
            map.addLayer(PreferredAreaLayers.labels as SymbolLayer);
          break;
        case MapZonesType.Partition:
          !fillLayer && map.addLayer(PartitionsAreaLayers.fills as FillLayer);
          !borderLayer &&
            map.addLayer(PartitionsAreaLayers.borders as LineLayer);
          !labelLayer &&
            map.addLayer(PartitionsAreaLayers.labels as SymbolLayer);
          break;
        default:
          break;
      }
    }

    return () => {
      /* Cleanup whenever the Layers are re-assigned */
      mapHover.current && mapHover.current();
    };
  }, [map, props.countData, props.hideLabel, props.type, props.data]);

  /* setFeatureState */
  /* This is used for updating the Zones layer */
  useEffect(() => {
    /* Do nothing when the initial layers haven't been assigned */
    if (!dataRef.current) {
      return;
    }

    /* Remove oldFeatureState */
    map.removeFeatureState({ source: MapZonesOverlay.SourceId });

    /* Set the Zones layer feature state for coloring the zones based on the user conditional */
    if (props.objects?.length > 0) {
      props.objects.map(featureStateObject => {
        map.setFeatureState(
          {
            source: MapZonesOverlay.SourceId,
            id: parseInt(featureStateObject.id), // feature id has to be int for mapbox to not lose the state
          },
          featureStateObject.value
        );

        return true;
      });
    }
  }, [map, props.objects]);

  /* onHoverZones */
  /* This is when the user is doing `hover` on the zones layer */
  useEffect(() => {
    /* Do nothing when the initial layers haven't been assigned */
    if (!dataRef.current) {
      return;
    }

    const mouseEnterAction = (
      evt: MapMouseEvent & { features?: MapboxGeoJSONFeature[] } & EventData
    ) => {
      /* Change the cursor style */
      map.getCanvas().style.cursor = 'pointer';
    };

    const updateFeatureStateForHover = (hover?: boolean) => {
      const newHoveredZoneObject: ZonesOverlayFeatureStateObject =
        props.objects.find(
          object => parseInt(object.id) === parseInt(hoveredZoneId.current.id)
        );

      newHoveredZoneObject &&
        map.setFeatureState(
          {
            source: MapZonesOverlay.SourceId,
            id: parseInt(hoveredZoneId.current.id), // feature id has to be int for mapbox to not lose the state
          },
          /* update the values based on latest `props.objects` value */
          { ...newHoveredZoneObject.value, hover }
        );
    };

    /* Highlight the hovered zone */
    /* This highlight detection is using `MouseMove` instead of `MouseEnter` */
    /* because we need to detect the mouse is currently on top of which zones */
    const mousemoveAction = (
      evt: MapMouseEvent & { features?: MapboxGeoJSONFeature[] } & EventData
    ) => {
      const hasEventFeatures = evt.features?.length > 0;

      /**
       * Note :
       * `selectedZone` should use the ID from properties to prevent the string round-up.
       * e.g.
       * ID = "030160" -> from properties `evt.features[0].properties.id`
       * if we take from the object's ID `evt.features[0].id`, it will became 30160 (number)
       */
      const isSameZone =
        hoveredZoneId.current?.id === evt.features[0].properties.id;

      if (hasEventFeatures && !isSameZone) {
        /* Remove the previous highlight */
        hoveredZoneId.current && updateFeatureStateForHover(false);

        /* Update which zone is the cursor currently located */
        hoveredZoneId.current = {
          id: evt.features[0].properties.id,
          value: evt.features[0].state,
        };

        /* Add the new hightlight */
        hoveredZoneId.current && updateFeatureStateForHover(true);
      }
    };

    const mouseleaveAction = (
      evt: MapMouseEvent & { features?: MapboxGeoJSONFeature[] } & EventData
    ) => {
      /* Remove all hover highlight whenever user's mouse does not in the zones layer anymore */
      hoveredZoneId.current && updateFeatureStateForHover(false);
    };

    map.on('mouseenter', MapZonesOverlay.FillLayerId, mouseEnterAction);
    map.on('mousemove', MapZonesOverlay.FillLayerId, mousemoveAction);
    map.on('mouseleave', MapZonesOverlay.FillLayerId, mouseleaveAction);

    mapHover.current = () => {
      map.off('mouseenter', MapZonesOverlay.FillLayerId, mouseEnterAction);
      map.off('mousemove', MapZonesOverlay.FillLayerId, mousemoveAction);
      map.off('mouseleave', MapZonesOverlay.FillLayerId, mouseleaveAction);

      mapHover.current = undefined;
    };

    return () => {
      mapHover.current && mapHover.current();
    };
  }, [map, props.objects]);

  /* Add the Map listener to detect the zone selection */
  useEffect(() => {
    /* Do nothing when the initial layers haven't been assigned */
    if (!dataRef.current) {
      return;
    }

    const onClickAction = (
      evt: MapMouseEvent & { features?: MapboxGeoJSONFeature[] } & EventData
    ) => {
      /**
       * Note :
       * `selectedZone` should use the ID from properties to prevent the string round-up.
       * e.g.
       * ID = "030160" -> from properties `evt.features[0].properties.id`
       * if we take from the object's ID `evt.features[0].id`, it will became 30160 (number)
       */
      const selectedZone = evt.features[0].properties.id;
      const currentFeatureState = evt.features[0].state;

      if (selectedZone) {
        props.onClick(selectedZone, currentFeatureState);
      }
    };

    map.on('click', MapZonesOverlay.FillLayerId, onClickAction);

    return () => {
      map.off('click', MapZonesOverlay.FillLayerId, onClickAction);
    };
  }, [map, props]);

  /* Hide Label */
  useEffect(() => {
    const labelLayer = map.getLayer(MapZonesOverlay.LabelLayerId);
    if (props.hideLabel) {
      labelLayer && map.removeLayer(MapZonesOverlay.LabelLayerId);
    } else {
      !labelLayer && map.addLayer(PartitionsAreaLayers.labels as SymbolLayer);
    }
  }, [map, props.hideLabel]);

  return <></>;
};
